import * as React from "react";
import Grid2 from "@mui/material/Unstable_Grid2";
import { Button, IconButton, Typography, useTheme } from "@mui/material";
import { TableDef, TableWrapper } from "../framework/tableWrapper";
import { useEffect, useMemo, useState } from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import * as workTimeTypeService from "../../services/workTimeTypeService";
import * as workTimeBetaService from "../../services/workTimeBetaService";
import { getErrorMessageFromError } from "../../services/baseService";
import { handleApiError } from "../../models/store/storeEffects";
import { WorkTimeTypeListItem } from "../../models/workTimeType/workTimeTypeListItem";
import { ISalaryRowType } from "../../models/workShitTimeSlot/salaryRowType";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
} from "@mui/icons-material";
import { SettingsWorkTimeTypeDialog } from "./settingsWorkTimeTypeDialog";
import { WorkTimeTypeEditItem } from "../../models/workTimeType/workTimeTypeEditItem";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { useTranslation } from "react-i18next";
import CheckIcon from "@mui/icons-material/Check";

export function SettingsWorkTimeTypeList() {
    const [items, setItems] = useState<WorkTimeTypeListItem[]>([]);
    const [item, setItem] = useState<WorkTimeTypeEditItem | null>();
    const [defaultValuesOnly, setDefaultValuesOnly] = useState<boolean>(false);
    const [salaryRowTypes, setSalaryRowTypes] = useState<ISalaryRowType[]>([]);

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const dispatch = store.customStore.dispatch;
    const theme = useTheme();
    const { t } = useTranslation();

    const openCreateDialog = () => {
        setItem(new WorkTimeTypeEditItem());
        setShowDialog(true);
    };

    const handleClose = () => {
        setItem(null);
        setShowDialog(false);
        fetchItems();
    };
    const openEditDialog = (workTimeType: WorkTimeTypeListItem) => {
        setItem(new WorkTimeTypeEditItem(workTimeType));
        setShowDialog(true);
    };

    const fetchItems = () => {
        dispatch(storeActions.fetchStart());
        workTimeTypeService
            .getWorkTimeTypes()
            .then(
                (result) => {
                    setItems(result);
                },
                (error) => {
                    handleApiError(error, dispatch);
                }
            )
            .finally(() => dispatch(storeActions.fetchEnd()));
    };

    const fetchSalaryRowTypes = () => {
        workTimeBetaService
            .getSalaryRowTypes()
            .then((result) => setSalaryRowTypes(result))
            .catch((error) => handleApiError(error, dispatch));
    };

    useEffect(fetchItems, []);
    useEffect(fetchSalaryRowTypes, []);

    useEffect(() => {
        if (!items) {
            return;
        }
        const defaultValuesOnly = items.every((x) => x.ownerId === null);
        setDefaultValuesOnly(defaultValuesOnly);
    }, [items]);

    const handleDelete = (workTimeTypeId: string, workTimeTypeName: string) => {
        dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("workTime.typeRemoveConfirmation", { p0: workTimeTypeName }),
                () => {
                    dispatch(storeActions.fetchStart());

                    workTimeTypeService
                        .deleteWorkTimeType(workTimeTypeId)
                        .then(
                            () => {
                                fetchItems();
                                dispatch(
                                    storeActions.showSuccessMessage(
                                        t("workTime.typeRemoveSuccess", {
                                            p0: workTimeTypeName,
                                        })
                                    )
                                );
                            },
                            (error) => {
                                dispatch(
                                    storeActions.showErrorMessage(
                                        t(
                                            "workTime.typeRemoveFailedErrorParameter",
                                            {
                                                p0: workTimeTypeName,
                                                p1: getErrorMessageFromError(
                                                    error
                                                ),
                                            }
                                        )
                                    )
                                );
                            }
                        )
                        .finally(() => {
                            dispatch(storeActions.fetchEnd());
                            dispatch(storeActions.clearConfirmation());
                        });
                }
            )
        );
    };

    const table = useMemo<TableDef<WorkTimeTypeListItem>>(
        () => ({
            columns: [
                {
                    label: t("workTime.type"),
                    accessor: "name",
                },
                {
                    label: t("workTimeType.type"),
                    accessor: (row) =>
                        t(`workTimeType.types.${row.type}`).toString(),
                },
                {
                    label: t("workTimeType.calculateOvertime"),
                    renderCell: (_, row) =>
                        row.calculateOvertime ? (
                            <CheckIcon color="success" />
                        ) : null,
                },
                {
                    label: t("workTimeType.calculateAddition"),
                    renderCell: (_, row) =>
                        row.calculateAddition ? (
                            <CheckIcon color="success" />
                        ) : null,
                },
                {
                    label: t("workTimeType.isCommentInUse"),
                    renderCell: (_, row) =>
                        row.isCommentInUse ? (
                            <CheckIcon color="success" />
                        ) : null,
                },
                {
                    label: t("workTimeType.finishWorkShiftSetting"),
                    renderCell: (_, row) =>
                        row.finishesWorkShift ? (
                            <CheckIcon color="success" />
                        ) : null,
                },
                {
                    label: t("workTimeType.salaryRowType"),
                    accessor: (row) => row.salaryRowTypeName || "",
                },
                {
                    label: "",
                    renderCell: (_, row) =>
                        !row.ownerId && <div>{t("common.default")}</div>,
                },
                {
                    label: "",
                    renderCell: (_, row) =>
                        row.ownerId && (
                            <Grid2 container justifyContent="flex-end">
                                <IconButton
                                    size="small"
                                    onClick={() => openEditDialog(row)}
                                >
                                    <EditIcon color="primary" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() =>
                                        handleDelete(
                                            row.workTimeTypeId,
                                            row.name
                                        )
                                    }
                                >
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Grid2>
                        ),
                },
            ],
        }),
        [t]
    );

    const initializeWorkTimeTypes = () => {
        dispatch(
            storeActions.setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                t("common.initializeWorkTimeTypesConfirmation"),
                () => {
                    dispatch(storeActions.fetchStart());

                    workTimeTypeService
                        .initializeWorkTimeTypes()
                        .then(
                            (result) => {
                                setItems(result);
                                dispatch(
                                    storeActions.showSuccessMessage(
                                        t(
                                            "common.initializeWorkTimeTypesSuccess"
                                        )
                                    )
                                );
                            },
                            (error) => {
                                dispatch(
                                    storeActions.showErrorMessage(
                                        t(
                                            "common.initializeWorkTimeTypesFailedErrorParameter",
                                            { p0: error }
                                        )
                                    )
                                );
                            }
                        )
                        .finally(() => {
                            dispatch(storeActions.fetchEnd());
                            dispatch(storeActions.clearConfirmation());
                        });
                }
            )
        );
    };
    return (
        <Grid2 sx={{ padding: "1rem" }}>
            <Typography variant="h3">{t("workTime.types")}</Typography>
            <Grid2
                xs={12}
                container
                spacing={2}
                alignItems="center"
                justifyContent="flex-end"
            >
                {defaultValuesOnly && (
                    <Button
                        color="secondary"
                        sx={{ color: theme.palette.common.white }}
                        variant={"contained"}
                        onClick={initializeWorkTimeTypes}
                    >
                        <AddIcon />
                        {t("common.initializeWorkTimeTypes")}
                    </Button>
                )}
                {!defaultValuesOnly && (
                    <Button
                        color="secondary"
                        sx={{ color: theme.palette.common.white }}
                        variant={"contained"}
                        onClick={openCreateDialog}
                    >
                        <AddIcon />
                        {t("common.addNew")}
                    </Button>
                )}
            </Grid2>
            <TableWrapper tableDef={table} data={items} />
            {showDialog && (
                <SettingsWorkTimeTypeDialog
                    item={item}
                    open={showDialog}
                    onClose={handleClose}
                    salaryRowTypes={salaryRowTypes}
                />
            )}
        </Grid2>
    );
}
